/* ScheduleComponent.js */

"use strict";
const _ = require("lodash");
const DateFormat = require("dateformat");
const Renderer = require("./ScheduleRenderer");
const BaseScheduleComponent = require("./base/BaseScheduleComponent");
const moment = require("moment-timezone");

const CommonDateTimeCondition = {
    time: {includeDate: false, includeTime: true},
    start: {includeDate: true, includeTime: false},
    end: {includeDate: true, includeTime: false},
};
const DateTimeConditions = {
    one: {
        on: {includeDate: true, includeTime: true},
    },
    poll: {
        start: {includeDate: true, includeTime: true},
        end: {includeDate: true, includeTime: true},
        time: {includeDate: false, includeTime: true},
    },
    dow: CommonDateTimeCondition,
    day: CommonDateTimeCondition,
    dom: CommonDateTimeCondition,
    dowom: CommonDateTimeCondition,
    domoy: CommonDateTimeCondition,
    dowomoy: CommonDateTimeCondition,
    avail: {
        before: {includeDate: false, includeTime: true},
        after: {includeDate: false, includeTime: true},
        start: {includeDate: true, includeTime: false},
        end: {includeDate: true, includeTime: false},
    },
};

const ScheduleTypeComponents = {
    one: {compClass: require("./schedule_components/one_time_component/OneTimeComponent")},
    poll: {
        compClass: require("./schedule_components/repeat_hour_component/RepeatHourComponent"),
        repeatOption: "repeatHour",
    },
    day: {
        compClass: require("./schedule_components/repeat_day_component/RepeatDayComponent"),
        repeatOption: "repeatDay",
    },
    dow: {
        compClass: require("./schedule_components/repeat_week_component/RepeatWeekComponent"),
        repeatOption: "repeatWeek",
    },
    dom: {
        compClass: require("./schedule_components/repeat_date_of_month_component/RepeatDateOfMonthComponent"),
        repeatOption: "repeatMonth",
        dateOrWeekOption: "date",
    },
    dowom: {
        compClass: require("./schedule_components/repeat_day_of_week_of_month_component/RepeatDayOfWeekOfMonthComponent"),
        repeatOption: "repeatMonth",
        dateOrWeekOption: "week",
    },
    domoy: {
        compClass: require("./schedule_components/repeat_date_of_month_of_year_component/RepeatDateOfMonthOfYearComponent"),
        repeatOption: "repeatYear",
        dateOrWeekOption: "date",
    },
    dowomoy: {
        compClass: require("./schedule_components/repeat_day_of_week_of_month_of_year_component/RepeatDayOfWeekOfMonthOfYearComponent"),
        repeatOption: "repeatYear",
        dateOrWeekOption: "week",
    },
    avail: {
        compClass: require("./schedule_components/avail_component/AvailComponent"),
        repeatOption: "avail",
    },
};

class ScheduleComponent extends BaseScheduleComponent {
    constructor(schedule, options) {
        super(Renderer);
        this._schedule = _.cloneDeep(schedule) || ScheduleComponent.defaultValue(options);
        this._origSchedule = _.cloneDeep(schedule) || ScheduleComponent.defaultValue(options);
        this._originTz = undefined;
        this._originalDt = undefined;
        this._firstRun = true;
        this._options = options || {
            allowOne: true,
            allowRepeat: true,
            pollOnly: false,
            availOnly: false,
            disabled: false,
            noStartingEnding: false,
        };
    }
    get options() {
        return this.getOptions();
    }
    getOptions() {
        return this._options;
    }

    set allowTypeChoice(allowChoice) {
        this.setAllowTypeChoice(allowChoice);
    }

    setAllowTypeChoice(allowChoice) {
        if (allowChoice) {
            this._options.allowOne = true;
            this._options.allowRepeat = true;
        } else {
            this._options.allowRepeat = false;
        }
        this.emit("TypeChoice", allowChoice);
    }

    toggleScheduleAvailabilityOption() {
        if (typeof this._options.disabled !== "undefined") {
            this._options.disabled = !this._options.disabled;
        } else {
            this._options.disabled = false;
        }
        this.emit("ToggleScheduleAvailability", this._options.disabled);
    }

    setDisabledSchedule(isDisabled) {
        this._options.disabled = isDisabled ? isDisabled : false;
        this.emit("ToggleScheduleAvailability", this._options.disabled);
    }

    get allowTypeChoice() {
        return this.isAllowTypeChoice();
    }

    isAllowTypeChoice() {
        return this._options.allowRepeat && this._options.allowOne;
    }

    getScheduleTypeInfo(schedType) {
        return ScheduleTypeComponents[schedType];
    }
    get schedule() {
        return this.getSchedule();
    }
    getSchedule() {
        return this._schedule;
    }
    set schedule(schedule) {
        this.setSchedule(schedule);
    }
    setSchedule(schedule, wasSetByUser) {
        let oldSched = this._schedule;
        this._schedule = schedule;
        this.emit(wasSetByUser ? "UserValue" : "ReloadRender", this._schedule, oldSched);
    }
    getScheduleProp(propName) {
        return _.get(this._schedule, propName);
    }
    setScheduleProp(propName, val, wasSetByUser) {
        let oldSched = _.cloneDeep(this._schedule);
        if (val === undefined) {
            _.unset(this._schedule, propName);
        } else {
            if (val instanceof Date) {
                this.setDateTimeValue(propName, val, wasSetByUser);
            } else {
                _.set(this._schedule, propName, val);
            }
        }
        this.emit(wasSetByUser ? "UserValue" : "Value", this._schedule, oldSched);
    }
    setScheduleProps(nameVals, wasSetByUser) {
        let oldSched = _.cloneDeep(this._schedule);
        _.forEach(nameVals, (val, propName) => {
            if (val === undefined) {
                _.unset(this._schedule, propName);
            } else {
                if (val instanceof Date) {
                    this.setDateTimeValue(propName, val, wasSetByUser);
                } else {
                    _.set(this._schedule, propName, val);
                }
            }
        });
        this.emit(wasSetByUser ? "UserValue" : "Value", this._schedule, oldSched);
    }
    dateTimePropToDate(propName) {
        let schedDateTime = this.getScheduleProp(propName);
        if (schedDateTime) {
            let now = new Date();
            const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const fromDbTz = _.get(this._schedule, "tz", false);
            // let dt = new Date(
            //     schedDateTime.year || now.getFullYear(),
            //     schedDateTime.hasOwnProperty('month') ? schedDateTime.month : now.getMonth(),
            //     schedDateTime.date || now.getDate(),
            //     schedDateTime.hour || 0,
            //     schedDateTime.minute || 0
            // );
            const yr = schedDateTime.year || now.getFullYear(),
                mo = Object.hasOwn(schedDateTime, "month")
                    ? schedDateTime.month + 1
                    : now.getMonth() + 1,
                d = schedDateTime.date || now.getDate(),
                h = schedDateTime.hour || 0,
                min = schedDateTime.minute || 0;

            let timeStr = `${yr}-${mo < 10 ? "0" + mo : mo}-${d < 10 ? "0" + d : d} ${
                h < 10 ? "0" + h : h
            }:${min < 10 ? "0" + min : min}`;
            let dt = moment.tz(timeStr, fromDbTz);
            let returnDt = dt.clone().tz(userTz);
            if (this._firstRun) {
                this._originTz = fromDbTz;
                this._originalDt = returnDt.toDate();
                this._firstRun = false;
            }
            if (fromDbTz !== userTz) {
                this.setScheduleProp("tz", userTz);
                if (!_.isNil(schedDateTime.hour)) {
                    schedDateTime.hour = returnDt.hour();
                }
                if (!_.isNil(schedDateTime.minute)) {
                    schedDateTime.minute = returnDt.minutes();
                }
                if (!_.isNil(schedDateTime.month)) {
                    schedDateTime.month = returnDt.month();
                }
                if (!_.isNil(schedDateTime.date)) {
                    schedDateTime.date = returnDt.date();
                }
            }
            return returnDt.toDate();
        }
    }

    getScheduleAtTimeZone(schedule, tz) {
        const yr = _.get(schedule, "start.year", undefined);
        let mo = _.get(schedule, "start.month", undefined);
        const d = _.get(schedule, "start.date", undefined);
        const h = _.get(schedule, "time.hour", undefined);
        const min = _.get(schedule, "time.minute", undefined);

        if (mo !== undefined) {
            mo = parseInt(mo, 10) + 1;
        }
        let convertedSchedule = _.cloneDeep(schedule);
        let timeStr = `${yr}-${mo < 10 ? "0" + mo : mo}-${d < 10 ? "0" + d : d} ${
            h < 10 ? "0" + h : h
        }:${min < 10 ? "0" + min : min}`;
        try {
            let momentDt = moment.tz(timeStr, schedule.tz);
            let convertedMomentDt = momentDt.clone().tz(tz);
            convertedSchedule.start = {
                year: convertedMomentDt.year(),
                month: convertedMomentDt.month(),
                date: convertedMomentDt.date(),
            };
            convertedSchedule.time = {
                hour: convertedMomentDt.hour(),
                minute: convertedMomentDt.minutes(),
            };
            convertedSchedule.tz = tz;
            return convertedSchedule;
        } catch (err) {
            console.log(err);
        }
    }

    getDateTimeString(propName) {
        let date = this.dateTimePropToDate(propName) || new Date();
        let cond = this.getDateTimeCondition(propName);
        if (cond) {
            if (cond.includeDate && cond.includeTime) {
                return DateFormat(date, "dddd, mmmm dS, yyyy, h:MM TT Z");
            } else if (cond.includeDate) {
                return DateFormat(date, "dddd, mmmm dS, yyyy");
            } else if (cond.includeTime) {
                return DateFormat(date, "h:MM TT Z");
            }
        }
    }

    getDateTime(propName) {
        let date = this.dateTimePropToDate(propName) || new Date();
        // let cond = this.getDateTimeCondition(propName);
        // if (cond) {
        //     if (cond.includeDate && cond.includeTime) {
        //         return DateFormat(date, 'dddd, mmmm dS, yyyy, h:MM TT Z');
        //     } else if (cond.includeDate) {
        //         return DateFormat(date, 'dddd, mmmm dS, yyyy');
        //     } else if (cond.includeTime) {
        //         return DateFormat(date, 'h:MM TT Z');
        //     }
        // }
        return date;
    }
    setInitial(val) {
        this._firstRun = val;
    }
    setDateTimeValue(propName, dateTimeVal, wasSetByUser) {
        let cond = this.getDateTimeCondition(propName);
        if (cond) {
            let schedDateTime = {};
            if (dateTimeVal instanceof Date) {
                // console.log(dateTimeVal);
                const currTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
                const formattedDateVal = moment
                    .tz(dateTimeVal.toISOString(), currTz)
                    .format("YYYY-MM-DD HH:mm");
                const formattedOriginalDt = moment
                    .tz(
                        this._originalDt
                            ? this._originalDt.toISOString()
                            : new Date().toISOString(),
                        currTz
                    )
                    .format("YYYY-MM-DD HH:mm");
                if (formattedDateVal === formattedOriginalDt) {
                    dateTimeVal = moment.tz(
                        this._originalDt
                            ? this._originalDt.toISOString()
                            : new Date().toISOString(),
                        this._originTz
                    );
                    this._schedule.tz = this._originTz;
                } else {
                    dateTimeVal = moment.tz(dateTimeVal.toISOString(), currTz);
                }
                // if(wasSetByUser) {
                //     this._schedule.tz = this._originTz;
                //     console.log(moment.tz(dateTimeVal, this._originTz).tz());
                // }

                schedDateTime = {};
                if (cond.includeDate) {
                    schedDateTime.year = dateTimeVal.year();
                    schedDateTime.month = dateTimeVal.month();
                    schedDateTime.date = dateTimeVal.date();
                }
                if (cond.includeTime) {
                    schedDateTime.hour = dateTimeVal.hours();
                    schedDateTime.minute = dateTimeVal.minutes();
                }
            } else if (_.isObject(dateTimeVal)) {
                if (cond.includeDate) {
                    schedDateTime.year = dateTimeVal.year;
                    schedDateTime.month = dateTimeVal.month;
                    schedDateTime.date = dateTimeVal.date || dateTimeVal.day;
                }
                if (cond.includeTime) {
                    schedDateTime.hour = dateTimeVal.hour || dateTimeVal.hours;
                    schedDateTime.minute = dateTimeVal.minute || dateTimeVal.minutes;
                }
            }
            this.setScheduleProp(propName, schedDateTime, wasSetByUser);
        }
    }
    getDateTimeCondition(propName) {
        return _.get(DateTimeConditions, [this._schedule.type, propName]);
    }
    validateSchedule() {
        let validationErrors = [];
        if (this._schedule) {
            let now = Date.now();
            if (this._schedule.type === "one" && this._schedule.on) {
                let on = this._schedule.on;
                let onDate = new Date(on.year, on.month, on.date, on.hour, on.minute, 0);
                if (onDate.getTime() <= now) {
                    validationErrors.push("The occurrence date/time must be in the future");
                }
            } else {
                if (this._schedule.start && this._schedule.end) {
                    let start = this._schedule.start;
                    let end = this._schedule.end;
                    let startDate = new Date(
                        start.year,
                        start.month,
                        start.date,
                        start.hour || 0,
                        start.minute || 0,
                        0
                    );
                    let endDate = new Date(
                        end.year,
                        end.month,
                        end.date,
                        end.hour || 0,
                        end.minute || 0,
                        0
                    );
                    if (endDate.getTime() <= startDate.getTime()) {
                        validationErrors.push(
                            "The end date/time must be after the start date/time"
                        );
                    }
                    if (endDate.getTime() <= now) {
                        validationErrors.push("The end date/time must be in the future");
                    }
                }
            }
        }
        return validationErrors;
    }

    static defaultValue(opts) {
        let defaultType = opts
            ? opts.allowOne
                ? "one"
                : opts.allowRepeat
                ? "day"
                : opts.pollOnly
                ? "poll"
                : opts.availOnly
                ? "avail"
                : "one"
            : "one";
        return ScheduleTypeComponents[defaultType].compClass.defaultValue();
    }
}
module.exports = ScheduleComponent;
